import { Link } from 'react-router-dom'
import { IconChevronDown } from '@tabler/icons-react'
import { Button, buttonVariants } from '../custom/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip'
import { cn } from '@/lib/utils'
import useCheckActiveNav from '@/hooks/use-check-active-nav'
import { SideLink } from '@/types/utility.types'
import { useTranslation } from 'react-i18next'
import childRoutes from '@/routes'

// Map each route's path to its `lazy()` importer so sidebar links can
// warm the route chunk on hover, ahead of the actual click/navigation.
const routeLazyMap = new Map(
  childRoutes.map((route) => [route.path, route.lazy] as const)
)

// Dynamic imports are cached by the bundler/browser, so calling this
// repeatedly (e.g. on repeated hovers) is safe and cheap.
const prefetchRoute = (href: string) => {
  routeLazyMap
    .get(href)?.()
    .catch(() => {})
}

interface NavProps extends React.HTMLAttributes<HTMLDivElement> {
  isCollapsed: boolean
  links: SideLink[]
  closeNav: () => void
}

export default function Nav({
  links,
  isCollapsed,
  className,
  closeNav,
}: NavProps) {
  const { t } = useTranslation('global')
  const renderLink = ({ sub, title, ...rest }: SideLink) => {
    const translatedTitle = t(title) // Translate the title
    const key = `${translatedTitle}-${rest.href}`
    if (isCollapsed && sub)
      return (
        <NavLinkIconDropdown
          {...rest}
          title={translatedTitle} // Pass translated title
          sub={sub}
          key={translatedTitle}
          closeNav={closeNav}
        />
      )

    if (isCollapsed)
      return (
        <NavLinkIcon
          {...rest}
          key={key}
          title={translatedTitle} // Pass translated title
          closeNav={closeNav}
        />
      )

    if (sub)
      return (
        <NavLinkDropdown
          {...rest}
          sub={sub}
          key={key}
          title={translatedTitle} // Pass translated title
          closeNav={closeNav}
        />
      )

    return (
      <NavLink
        {...rest}
        key={key}
        title={translatedTitle} // Pass translated title
        closeNav={closeNav}
      />
    )
  }
  return (
    <div
      data-collapsed={isCollapsed}
      className={cn(
        'group border-b bg-transparent py-2 transition-[max-height,padding] duration-500 data-[collapsed=true]:py-2 md:border-none',
        className
      )}
    >
      <TooltipProvider delayDuration={0}>
        <nav className='grid gap-1 group-[[data-collapsed=true]]:justify-center group-[[data-collapsed=true]]:px-2'>
          {links.map(renderLink)}
        </nav>
      </TooltipProvider>
    </div>
  )
}

interface NavLinkProps extends SideLink {
  subLink?: boolean
  closeNav: () => void
}

function NavLink({
  title,
  icon,
  label,
  href,
  closeNav,
  subLink = false,
}: NavLinkProps) {
  const { t } = useTranslation('global')
  const translatedTitle = t(`global:sidebar.${title.toLowerCase()}`)
  const { checkActiveNav } = useCheckActiveNav()

  return (
    <Link
      to={href}
      onClick={closeNav}
      onMouseEnter={() => prefetchRoute(href)}
      onPointerDown={() => prefetchRoute(href)}
      className={cn(
        buttonVariants({
          variant: checkActiveNav(href) ? 'secondary' : 'ghost',
          size: 'lg',
        }),
        'h-12 justify-start text-wrap rounded-none px-6',
        subLink && 'h-10 w-full border-l border-l-slate-500 px-2'
      )}
      aria-current={checkActiveNav(href) ? 'page' : undefined}
    >
      <div className='mr-2'>{icon}</div>
      {/* {title} */}
      {translatedTitle}
      {label && (
        <div className='ml-2 rounded-lg bg-primary px-1 text-[0.625rem] text-primary-foreground'>
          {label}
        </div>
      )}
    </Link>
  )
}

function NavLinkDropdown({ title, icon, label, sub, closeNav }: NavLinkProps) {
  const { t } = useTranslation('global')
  const translatedTitle = t(`global:sidebar.${title.toLowerCase()}`)
  const { checkActiveNav } = useCheckActiveNav()

  /* Open collapsible by default
   * if one of child element is active */
  const isChildActive = !!sub?.find((s) => checkActiveNav(s.href))

  return (
    <Collapsible defaultOpen={isChildActive}>
      <CollapsibleTrigger
        className={cn(
          buttonVariants({ variant: 'ghost', size: 'lg' }),
          'group h-12 w-full justify-start rounded-none px-6'
        )}
      >
        <div className='mr-2'>{icon}</div>
        {/* {title} */}
        {translatedTitle}
        {label && (
          <div className='ml-2 rounded-lg bg-primary px-1 text-[0.625rem] text-primary-foreground'>
            {label}
          </div>
        )}
        <span
          className={cn(
            'ml-auto transition-all group-data-[state="open"]:-rotate-180'
          )}
        >
          <IconChevronDown stroke={1} />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className='collapsibleDropdown' asChild>
        <ul>
          {sub!.map((sublink) => (
            <li key={sublink.title} className='my-1 ml-8'>
              <NavLink {...sublink} subLink closeNav={closeNav} />
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}

function NavLinkIcon({ title, icon, label, href }: NavLinkProps) {
  const { checkActiveNav } = useCheckActiveNav()
  const { t } = useTranslation(['global'])
  const translatedTitle = t(`sidebar.${title.toLowerCase()}`)
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Link
          to={href}
          onMouseEnter={() => prefetchRoute(href)}
          onPointerDown={() => prefetchRoute(href)}
          className={cn(
            buttonVariants({
              variant: checkActiveNav(href) ? 'secondary' : 'ghost',
              size: 'icon',
            }),
            'h-12 w-12'
          )}
        >
          {icon}
          <span className='sr-only'>{translatedTitle}</span>
        </Link>
      </TooltipTrigger>
      <TooltipContent side='right' className='flex items-center gap-4'>
        {translatedTitle}
        {label && (
          <span className='ml-auto text-muted-foreground'>{label}</span>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

function NavLinkIconDropdown({ title, icon, label, sub }: NavLinkProps) {
  const { checkActiveNav } = useCheckActiveNav()

  const { t } = useTranslation(['global'])
  const translatedTitle = t(`sidebar.${title.toLowerCase()}`)

  /* Open collapsible by default
   * if one of child element is active */
  const isChildActive = !!sub?.find((s) => checkActiveNav(s.href))

  return (
    <DropdownMenu>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant={isChildActive ? 'secondary' : 'ghost'}
              size='icon'
              className='h-12 w-12'
            >
              {icon}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side='right' className='flex items-center gap-4'>
          {translatedTitle}{' '}
          {label && (
            <span className='ml-auto text-muted-foreground'>{label}</span>
          )}
          <IconChevronDown
            size={18}
            className='-rotate-90 text-muted-foreground'
          />
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent side='right' align='start' sideOffset={4}>
        <DropdownMenuLabel>
          {translatedTitle} {label ? `(${label})` : ''}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {sub!.map(({ title, icon, label, href }) => (
          <DropdownMenuItem key={`${title}-${href}`} asChild>
            <Link
              to={href}
              onMouseEnter={() => prefetchRoute(href)}
              onPointerDown={() => prefetchRoute(href)}
              className={`${checkActiveNav(href) ? 'bg-secondary' : ''}`}
            >
              {icon}{' '}
              <span className='ml-2 max-w-52 text-wrap'>
                {t(`sidebar.${title.toLowerCase()}`)}
              </span>
              {label && <span className='ml-auto text-xs'>{label}</span>}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
